import SEO from "@/components/SEO";

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Privacy Policy - Stanford Logistics Management"
        description="Privacy Policy for Stanford Logistics Management. Learn how we collect, use, and protect your personal information."
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-primary-foreground/90">
              Last updated: November 17, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Introduction */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Introduction</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                Stanford Logistics Management ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                you visit our website or use our services.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">Information We Collect</h2>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-foreground">Personal Information</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside space-y-2 text-foreground/80 text-lg ml-4">
                  <li>Fill out contact forms on our website</li>
                  <li>Subscribe to our newsletter or updates</li>
                  <li>Request information about our services</li>
                  <li>Communicate with us via email or phone</li>
                </ul>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  This information may include your name, email address, phone number, company name, and any 
                  other information you choose to provide.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-foreground">Automatically Collected Information</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  When you visit our website, we may automatically collect certain information about your device, 
                  including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-foreground/80 text-lg ml-4">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Referring website</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Date and time of visit</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-foreground">Cookies and Tracking Technologies</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  We use cookies and similar tracking technologies to track activity on our website and store 
                  certain information. You can instruct your browser to refuse all cookies or to indicate when 
                  a cookie is being sent. However, if you do not accept cookies, you may not be able to use 
                  some portions of our website.
                </p>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">How We Use Your Information</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                We use the information we collect for various purposes, including to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-foreground/80 text-lg ml-4">
                <li>Respond to your inquiries and provide customer service</li>
                <li>Send you information about our services and industry insights</li>
                <li>Improve our website and user experience</li>
                <li>Analyze website usage and trends</li>
                <li>Prevent fraudulent activity and enhance security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            {/* How We Share Your Information */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">How We Share Your Information</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                We do not sell, trade, or rent your personal information to third parties. We may share your 
                information in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-3 text-foreground/80 text-lg ml-4">
                <li><strong className="text-foreground">Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf, such as website hosting, email delivery, and analytics.</li>
                <li><strong className="text-foreground">Legal Requirements:</strong> We may disclose information if required to do so by law or in response to valid requests by public authorities.</li>
                <li><strong className="text-foreground">Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Data Security</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. However, no method 
                of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee 
                absolute security.
              </p>
            </div>

            {/* Your Rights and Choices */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Your Rights and Choices</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-foreground/80 text-lg ml-4">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate information</li>
                <li>The right to request deletion of your information</li>
                <li>The right to object to or restrict processing</li>
                <li>The right to data portability</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p className="text-foreground/80 leading-relaxed text-lg mt-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Third-Party Links</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices or content of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Children's Privacy</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                Our website is not intended for children under the age of 13. We do not knowingly collect 
                personal information from children under 13. If you believe we have collected information from 
                a child under 13, please contact us immediately.
              </p>
            </div>

            {/* Changes to This Privacy Policy */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Changes to This Privacy Policy</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review 
                this Privacy Policy periodically for any changes.
              </p>
            </div>

            {/* Contact Us */}
            <div className="space-y-4 pb-8">
              <h2 className="text-3xl font-bold text-foreground">Contact Us</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-muted/30 p-6 rounded-lg border border-border">
                <p className="text-foreground font-semibold text-lg mb-3">Stanford Logistics Management</p>
                <p className="text-foreground/80 text-lg">
                  Email: <a href="mailto:info@stanfordlogistics.net" className="text-accent hover:underline font-medium">info@stanfordlogistics.net</a>
                </p>
                <p className="text-foreground/80 text-lg">
                  Phone: <a href="tel:+12014703182" className="text-accent hover:underline font-medium">201-470-3182</a>
                </p>
                <p className="text-foreground/80 text-lg">
                  Location: Savannah, GA
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
